import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../../helpers/url_helper';
import { useThemeMode } from '../../ThemeContext';

const FolderIcon = ({ color = 'currentColor', size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const NewProjectIcon = ({ color = 'currentColor', size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v2" />
    <path d="M3 9v9a2 2 0 0 0 2 2h6" />
    <path d="M17 12v8" />
    <path d="M13 16h8" />
  </svg>
);

const PlusIcon = ({ color = 'currentColor', size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const MoreIcon = ({ color = 'currentColor', size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="5" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="19" cy="12" r="1.4" />
  </svg>
);

const KebabIcon = ({ color = 'currentColor', size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="5" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="12" cy="19" r="1.4" />
  </svg>
);

const ClockIcon = ({ color = 'currentColor', size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4l3 2" />
  </svg>
);

const SidebarArrowIcon = ({ color = 'currentColor', size = 20, direction = 'left' }) => {
  const rotation = direction === 'left' ? 90 : -90; // reuse down chevron path
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
};

const ChevronIcon = ({ color = 'currentColor', size = 20, direction = 'down' }) => {
  const rotation = direction === 'down' ? 0 : direction === 'up' ? 180 : direction === 'right' ? -90 : 90;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
};

/** Map API `history` row from conversation-projects list to shape Assistant.switchConversationToHistory expects. */
function mapRecentItemForAssistant(item) {
  if (!item || item.chat_session_id == null) return item;
  return {
    ...item,
    sessionId: item.chat_session_id,
    id: item.chat_session_id,
    title: item.title,
    messages: Array.isArray(item.history) ? item.history : [],
    createdAt: item.created_at
  };
}

export default function AssistantRightSidebar({
  mode: modeProp = 'light',
  brandIconSrc,
  collapsed: collapsedProp,
  onCollapsedChange,
  projects = [],
  recents = [],
  activeProjectId = null,
  activeRecentId = null,
  onProjectClick,
  onMoreProjects,
  onRecentClick,
  onNewChatClick
}) {
  const { mode: contextMode } = useThemeMode();
  const mode = contextMode || modeProp;
  const [collapsedState, setCollapsedState] = useState(false);
  const collapsed = typeof collapsedProp === 'boolean' ? collapsedProp : collapsedState;
  const setCollapsed = (next) => {
    const nextVal = typeof next === 'function' ? next(collapsed) : next;
    if (typeof collapsedProp !== 'boolean') setCollapsedState(nextVal);
    onCollapsedChange?.(nextVal);
  };
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [recentsOpen, setRecentsOpen] = useState(true);

  // Local "Create Project" popup (single Project title field)
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');
  const [projectTitleError, setProjectTitleError] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Conversation projects list (fetched from API)
  const [conversationProjects, setConversationProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  /** Chat sessions from the same list endpoint (`history` in response); Recents use this after a successful fetch. */
  const [chatHistoryFromApi, setChatHistoryFromApi] = useState([]);
  const [conversationListFetchSucceeded, setConversationListFetchSucceeded] = useState(false);
  const [hoveredProjectId, setHoveredProjectId] = useState(null);
  const [openMenuProjectId, setOpenMenuProjectId] = useState(null);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [isSavingProjectName, setIsSavingProjectName] = useState(false);
  const editInputRef = useRef(null);

  // Recents / chat history row (hover menu + inline title edit)
  const [hoveredRecentSessionKey, setHoveredRecentSessionKey] = useState(null);
  const [openMenuRecentSessionKey, setOpenMenuRecentSessionKey] = useState(null);
  const [editingRecentSessionKey, setEditingRecentSessionKey] = useState(null);
  const [editingRecentTitle, setEditingRecentTitle] = useState('');
  const [isSavingRecentTitle, setIsSavingRecentTitle] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const recentEditInputRef = useRef(null);

  const getRecentSessionKey = (r) => {
    const k = r?.chat_session_id ?? r?.sessionId ?? r?.id;
    return k != null ? String(k) : '';
  };

  const fetchConversationProjects = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setConversationProjects([]);
      setChatHistoryFromApi([]);
      setConversationListFetchSucceeded(false);
      setProjectsError('');
      return;
    }

    setIsLoadingProjects(true);
    setProjectsError('');

    const url = getApiUrl('company/conversation-projects/list');

    fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to load projects. Please try again.';
          throw new Error(msg);
        }
        const payload = data.data;
        let projectsList = [];
        let historyList = [];

        if (Array.isArray(payload)) {
          // Legacy: data is the project list; optional root-level history
          projectsList = payload;
          historyList = Array.isArray(data.history) ? data.history : [];
        } else if (payload && typeof payload === 'object') {
          // New: data object includes project array + history (do not drop projects)
          projectsList =
            Array.isArray(payload.conversation_projects)
              ? payload.conversation_projects
              : Array.isArray(payload.projects)
                ? payload.projects
                : Array.isArray(payload.data)
                  ? payload.data
                  : Array.isArray(payload.items)
                    ? payload.items
                    : [];
          historyList = Array.isArray(payload.history) ? payload.history : [];
        }

        setConversationProjects(projectsList);
        setChatHistoryFromApi(historyList);
        setConversationListFetchSucceeded(true);
      })
      .catch((err) => {
        setConversationProjects([]);
        setChatHistoryFromApi([]);
        setConversationListFetchSucceeded(false);
        setProjectsError(err.message || 'Failed to load projects. Please try again.');
      })
      .finally(() => {
        setIsLoadingProjects(false);
      });
  };

  useEffect(() => {
    fetchConversationProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingProjectId == null) return;
    const t = setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [editingProjectId]);

  useEffect(() => {
    if (editingRecentSessionKey == null) return;
    const t = setTimeout(() => {
      recentEditInputRef.current?.focus();
      recentEditInputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [editingRecentSessionKey]);

  const startEditProject = (project) => {
    const id = project?.conversation_project_id;
    if (!id) return;
    setOpenMenuProjectId(null);
    setEditingRecentSessionKey(null);
    setEditingProjectId(id);
    setEditingProjectName(project?.name || '');
  };

  const startEditRecent = (r) => {
    const key = getRecentSessionKey(r);
    if (!key) return;
    setOpenMenuRecentSessionKey(null);
    setOpenMenuProjectId(null);
    setEditingProjectId(null);
    setEditingRecentSessionKey(key);
    setEditingRecentTitle(r?.title || 'Conversation');
  };

  const saveEditedRecentTitle = (r) => {
    const key = getRecentSessionKey(r);
    if (!key || isSavingRecentTitle) return;
    const newTitle = (editingRecentTitle || '').trim();
    const oldTitle = (r?.title || 'Conversation').trim();

    if (!newTitle) {
      setEditingRecentTitle(oldTitle);
      setEditingRecentSessionKey(null);
      return;
    }

    if (newTitle === oldTitle) {
      setEditingRecentSessionKey(null);
      return;
    }

    const sessionId = r?.chat_session_id ?? r?.sessionId ?? r?.id;
    if (sessionId == null) {
      setHistoryError('Cannot rename this chat (missing session id).');
      setEditingRecentSessionKey(null);
      return;
    }

    setIsSavingRecentTitle(true);
    setHistoryError('');
    const token = localStorage.getItem('authToken');
    const url = getApiUrl(`company/chat-sessions/${sessionId}/title`);

    fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ title: newTitle })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to update chat title. Please try again.';
          throw new Error(msg);
        }
        const resolved = data?.data?.title ?? newTitle;
        setChatHistoryFromApi((prev) =>
          prev.map((h) =>
            String(getRecentSessionKey(h)) === key ? { ...h, title: resolved } : h
          )
        );
      })
      .catch((err) => {
        setHistoryError(err.message || 'Failed to update chat title. Please try again.');
      })
      .finally(() => {
        setIsSavingRecentTitle(false);
        setEditingRecentSessionKey(null);
      });
  };

  const deleteRecentSession = (r) => {
    const sessionId = r?.chat_session_id ?? r?.sessionId ?? r?.id;
    const key = getRecentSessionKey(r);
    if (sessionId == null || !key) {
      setHistoryError('Cannot delete this chat (missing session id).');
      setOpenMenuRecentSessionKey(null);
      return;
    }

    setOpenMenuRecentSessionKey(null);
    setHistoryError('');
    const token = localStorage.getItem('authToken');
    const url = getApiUrl(`company/chat-sessions/${sessionId}`);

    fetch(url, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to delete chat. Please try again.';
          throw new Error(msg);
        }
        setChatHistoryFromApi((prev) =>
          prev.filter((h) => String(getRecentSessionKey(h)) !== key)
        );
        fetchConversationProjects();
      })
      .catch((err) => {
        setHistoryError(err.message || 'Failed to delete chat. Please try again.');
      });
  };

  const saveEditedProjectName = (project) => {
    const id = project?.conversation_project_id;
    if (!id || isSavingProjectName) return;
    const newName = (editingProjectName || '').trim();
    const oldName = (project?.name || '').trim();

    if (!newName) {
      setEditingProjectName(oldName);
      setEditingProjectId(null);
      return;
    }

    if (newName === oldName) {
      setEditingProjectId(null);
      return;
    }

    setIsSavingProjectName(true);
    const token = localStorage.getItem('authToken');
    const url = getApiUrl(`company/conversation-projects/${id}/name`);

    fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ name: newName })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to update project name. Please try again.';
          throw new Error(msg);
        }
        setConversationProjects((prev) =>
          prev.map((p) =>
            p?.conversation_project_id === id
              ? { ...p, name: data?.data?.name || newName }
              : p
          )
        );
        setProjectsError('');
      })
      .catch((err) => {
        setProjectsError(err.message || 'Failed to update project name. Please try again.');
      })
      .finally(() => {
        setIsSavingProjectName(false);
        setEditingProjectId(null);
      });
  };

  const deleteProject = (project) => {
    const id = project?.conversation_project_id;
    if (!id) {
      setProjectsError('Cannot delete this project (missing project id).');
      setOpenMenuProjectId(null);
      return;
    }

    setOpenMenuProjectId(null);
    setProjectsError('');
    const token = localStorage.getItem('authToken');
    const url = getApiUrl(`company/conversation-projects/${id}`);

    fetch(url, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to delete project. Please try again.';
          throw new Error(msg);
        }

        // Refresh projects and recents (chat sessions may also be removed by backend)
        fetchConversationProjects();
      })
      .catch((err) => {
        setProjectsError(err.message || 'Failed to delete project. Please try again.');
      });
  };

  const openProjectCreate = () => {
    setProjectTitle('');
    setProjectTitleError('');
    setShowProjectCreate(true);
  };

  const closeProjectCreate = () => {
    setShowProjectCreate(false);
    setProjectTitleError('');
  };

  const submitProjectCreate = () => {
    const title = (projectTitle || '').trim();
    if (!title) {
      setProjectTitleError('Project title is required');
      return;
    }
    if (isCreatingProject) return;
    setIsCreatingProject(true);

    const token = localStorage.getItem('authToken');
    const url = getApiUrl('company/conversation-projects/create');

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ name: title })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'success') {
          const msg =
            (data && (data.message || data.error)) ||
            'Failed to create project. Please try again.';
          throw new Error(msg);
        }
        // Success: close popup; projects list is static for now (can be refreshed later).
        setShowProjectCreate(false);
        fetchConversationProjects();
      })
      .catch((err) => {
        setProjectTitleError(err.message || 'Failed to create project. Please try again.');
      })
      .finally(() => {
        setIsCreatingProject(false);
      });
  };

  const colors = useMemo(() => {
    const isDark = mode === 'dark';
    return {
      bg: isDark ? '#0f0f0f' : '#ffffff',
      text: isDark ? '#ffffff' : '#000000',
      muted: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)',
      hover: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      active: isDark ? 'rgba(38,195,192,0.22)' : 'rgba(38,195,192,0.18)',
      stroke: isDark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.88)'
    };
  }, [mode]);

  const recentGroups = useMemo(() => {
    const source = conversationListFetchSucceeded ? chatHistoryFromApi : recents || [];

    const toDate = (r) => {
      const raw = r?.createdAt ?? r?.created_at ?? r?.updatedAt ?? r?.updated_at ?? null;
      if (!raw) return null;
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const todayStart = startOfDay(new Date());
    const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;

    const labeled = source.map((r, idx) => {
      const d = toDate(r);
      return { r, idx, d };
    });

    labeled.sort((a, b) => {
      if (a.d && b.d) return b.d.getTime() - a.d.getTime();
      if (a.d) return -1;
      if (b.d) return 1;
      return a.idx - b.idx;
    });

    const groups = [];
    const ensureGroup = (key, label) => {
      let g = groups.find((x) => x.key === key);
      if (!g) {
        g = { key, label, items: [] };
        groups.push(g);
      }
      return g;
    };

    labeled.forEach(({ r, d }) => {
      if (!d) {
        ensureGroup('unknown', 'Earlier').items.push(r);
        return;
      }
      const day = startOfDay(d);
      if (day === todayStart) ensureGroup('today', 'Today').items.push(r);
      else if (day === yesterdayStart) ensureGroup('yesterday', 'Yesterday').items.push(r);
      else {
        const label = d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
        ensureGroup(String(day), label).items.push(r);
      }
    });

    return groups;
  }, [conversationListFetchSucceeded, chatHistoryFromApi, recents]);

  return (
    <aside
      className={`assistant-right-sidebar assistant-right-sidebar-v2${collapsed ? ' collapsed' : ''}`}
      style={{ color: colors.text }}
    >
      <div className="assistant-right-sidebar-v2-inner">
        <div className="assistant-right-sidebar-v2-header">
          <button
            type="button"
            className="assistant-right-sidebar-v2-togglebtn"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'}
            title={collapsed ? 'Open sidebar' : 'Close sidebar'}
          >
            <SidebarArrowIcon
              color={colors.stroke}
              size={18}
              // Right sidebar: when open, arrow points right (collapse to edge).
              // When collapsed, arrow points left (expand into page).
              direction={collapsed ? 'left' : 'right'}
            />
          </button>

          {!collapsed && (
            <button
              type="button"
              className="assistant-right-sidebar-v2-plusbtn"
              onClick={() => onNewChatClick?.()}
              aria-label="New"
              title="New"
            >
              <PlusIcon color="#ffffff" size={18} />
            </button>
          )}
        </div>

        {collapsed && (
          <div className="assistant-right-sidebar-v2-rail">
            <button
              type="button"
              className="assistant-right-sidebar-v2-rail-btn"
              onClick={() => {
                setProjectsOpen(true);
                setRecentsOpen(true);
                setCollapsed(false);
                onNewChatClick?.();
              }}
              aria-label="New"
              title="New"
            >
              <span className="assistant-right-sidebar-v2-iconchip assistant-right-sidebar-v2-iconchip-plus">
                <PlusIcon color="#ffffff" size={18} />
              </span>
            </button>

            <button
              type="button"
              className="assistant-right-sidebar-v2-rail-btn"
              onClick={() => {
                setProjectsOpen(true);
                setRecentsOpen(true);
                setCollapsed(false);
              }}
              aria-label="Open Projects"
              title="Projects"
            >
              <span className="assistant-right-sidebar-v2-iconchip">
                <FolderIcon color={colors.stroke} size={20} />
              </span>
            </button>

            <button
              type="button"
              className="assistant-right-sidebar-v2-rail-btn"
              onClick={() => {
                setProjectsOpen(true);
                setRecentsOpen(true);
                setCollapsed(false);
              }}
              aria-label="Open Recents"
              title="Recents"
            >
              <span className="assistant-right-sidebar-v2-iconchip">
                <ClockIcon color={colors.stroke} size={20} />
              </span>
            </button>
          </div>
        )}

        {!collapsed && (
          <div className="assistant-right-sidebar-v2-content">
            <div className="assistant-right-sidebar-v2-section">
              <button
                type="button"
                className="assistant-right-sidebar-v2-accordion"
                onClick={() => setProjectsOpen((v) => !v)}
                style={{ color: colors.muted }}
                aria-expanded={projectsOpen}
              >
                <span className="assistant-right-sidebar-v2-accordion-left">
                  <span className="assistant-right-sidebar-v2-iconchip">
                    <FolderIcon color={colors.stroke} size={20} />
                  </span>
                  <span>Projects</span>
                </span>
                <ChevronIcon color={colors.stroke} direction={projectsOpen ? 'up' : 'down'} />
              </button>

              {projectsOpen && (
                <>
                  <button
                    type="button"
                    className="assistant-right-sidebar-v2-row"
                    onClick={openProjectCreate}
                    style={{ background: 'transparent', color: colors.text }}
                  >
                    <span className="assistant-right-sidebar-v2-row-icon">
                      <NewProjectIcon color={colors.stroke} />
                    </span>
                    <span className="assistant-right-sidebar-v2-row-text">New project</span>
                  </button>

                  {/* Conversation projects list: show up to 4, hide list & "More" if empty */}
                  {isLoadingProjects && (
                    <div
                      style={{
                        padding: '8px 14px',
                        fontSize: 12,
                        color: colors.muted
                      }}
                    >
                      Loading projects…
                    </div>
                  )}

                  {projectsError && !isLoadingProjects && (
                    <div
                      style={{
                        padding: '8px 14px',
                        fontSize: 12,
                        color: '#f44336'
                      }}
                    >
                      {projectsError}
                    </div>
                  )}

                  {conversationProjects.length > 0 && (
                    <div className="assistant-right-sidebar-v2-list">
                      {conversationProjects.slice(0, 4).map((p) => {
                        const id = p?.conversation_project_id;
                        const title = p?.name || 'Untitled';
                        const isActive =
                          id != null && String(id) === String(activeProjectId);
                        const isEditing = editingProjectId === id;
                        const showActions = hoveredProjectId === id || openMenuProjectId === id;

                        return (
                          <div
                            key={String(id ?? title)}
                            className={`assistant-right-sidebar-v2-row${
                              isActive ? ' active' : ''
                            }`}
                            title={title}
                            style={{
                              background: isActive ? colors.active : 'transparent',
                              color: colors.text,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              position: 'relative'
                            }}
                            onMouseEnter={() => setHoveredProjectId(id)}
                            onMouseLeave={() => setHoveredProjectId((prev) => (prev === id ? null : prev))}
                          >
                            <button
                              type="button"
                              onClick={() => onProjectClick?.(p)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: 'calc(100% - 28px)',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: colors.text
                              }}
                            >
                              <span className="assistant-right-sidebar-v2-row-icon">
                                <FolderIcon color={colors.stroke} />
                              </span>

                              {isEditing ? (
                                <input
                                  ref={editInputRef}
                                  value={editingProjectName}
                                  onChange={(e) => setEditingProjectName(e.target.value)}
                                  onBlur={() => saveEditedProjectName(p)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      saveEditedProjectName(p);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingProjectId(null);
                                      setEditingProjectName(title);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    height: 28,
                                    borderRadius: 6,
                                    border: `1px solid ${mode === 'dark' ? '#444' : '#ccc'}`,
                                    background: mode === 'dark' ? '#111' : '#fff',
                                    color: colors.text,
                                    padding: '0 8px',
                                    fontSize: 13,
                                    fontFamily: 'Urbanist, Arial, sans-serif'
                                  }}
                                />
                              ) : (
                                <span className="assistant-right-sidebar-v2-row-text">
                                  {title}
                                </span>
                              )}
                            </button>

                            {!isEditing && (
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuRecentSessionKey(null);
                                    setOpenMenuProjectId((prev) => (prev === id ? null : id));
                                  }}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    border: 'none',
                                    borderRadius: 6,
                                    display: showActions ? 'flex' : 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                    color: colors.stroke,
                                    cursor: 'pointer'
                                  }}
                                >
                                  <KebabIcon color={colors.stroke} size={14} />
                                </button>

                                {openMenuProjectId === id && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      position: 'absolute',
                                      top: 26,
                                      right: 0,
                                      minWidth: 120,
                                      borderRadius: 8,
                                      border: mode === 'dark' ? '1px solid #333' : '1px solid #e6e6e6',
                                      background: mode === 'dark' ? '#1a1a1a' : '#fff',
                                      boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                                      zIndex: 20,
                                      padding: 6
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startEditProject(p);
                                      }}
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: 6,
                                        color: colors.text,
                                        cursor: 'pointer',
                                        fontSize: 13
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteProject(p);
                                      }}
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: 6,
                                        color: '#f44336',
                                        cursor: 'pointer',
                                        fontSize: 13
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {conversationProjects.length > 4 && (
                        <button
                          type="button"
                          className="assistant-right-sidebar-v2-row"
                          onClick={onMoreProjects}
                          style={{ background: 'transparent', color: colors.text }}
                        >
                          <span className="assistant-right-sidebar-v2-row-icon">
                            <MoreIcon color={colors.stroke} />
                          </span>
                          <span className="assistant-right-sidebar-v2-row-text">More</span>
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="assistant-right-sidebar-v2-section">
              <button
                type="button"
                className="assistant-right-sidebar-v2-accordion"
                onClick={() => setRecentsOpen((v) => !v)}
                style={{ color: colors.muted }}
                aria-expanded={recentsOpen}
              >
                <span className="assistant-right-sidebar-v2-accordion-left">
                  <span className="assistant-right-sidebar-v2-iconchip">
                    <ClockIcon color={colors.stroke} size={20} />
                  </span>
                  <span>Recents</span>
                </span>
                <ChevronIcon color={colors.stroke} direction={recentsOpen ? 'up' : 'down'} />
              </button>

              {recentsOpen && (
                <div className="assistant-right-sidebar-v2-recents">
                  {historyError && (
                    <div
                      style={{
                        padding: '6px 14px 10px',
                        fontSize: 12,
                        color: '#f44336'
                      }}
                    >
                      {historyError}
                    </div>
                  )}
                  {recentGroups.map((g) => (
                    <div key={g.key} className="assistant-right-sidebar-v2-recentgroup">
                      <div className="assistant-right-sidebar-v2-recentgroup-title" style={{ color: colors.muted }}>
                        {g.label}
                      </div>
                      {g.items.map((r) => {
                        const sessionKey = getRecentSessionKey(r);
                        const id =
                          r?.chat_session_id ?? r?.id ?? r?.sessionId ?? r?.key ?? r?.title;
                        const title = r?.title || 'Conversation';
                        const isActive = id != null && String(id) === String(activeRecentId);
                        const isEditingRecent =
                          sessionKey && editingRecentSessionKey === sessionKey;
                        const showRecentActions =
                          sessionKey &&
                          (hoveredRecentSessionKey === sessionKey ||
                            openMenuRecentSessionKey === sessionKey);

                        return (
                          <div
                            key={String(id)}
                            className={`assistant-right-sidebar-v2-recent${isActive ? ' active' : ''}`}
                            title={title}
                            style={{
                              background: isActive ? colors.hover : 'transparent',
                              color: colors.text,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              position: 'relative',
                              padding: '6px 10px',
                              borderRadius: 8,
                              minHeight: 36
                            }}
                            onMouseEnter={() =>
                              sessionKey && setHoveredRecentSessionKey(sessionKey)
                            }
                            onMouseLeave={() =>
                              setHoveredRecentSessionKey((prev) =>
                                prev === sessionKey ? null : prev
                              )
                            }
                          >
                            <button
                              type="button"
                              onClick={() => onRecentClick?.(mapRecentItemForAssistant(r))}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: colors.text,
                                fontSize: 13,
                                fontFamily: 'Urbanist, Arial, sans-serif'
                              }}
                            >
                              {isEditingRecent ? (
                                <input
                                  ref={recentEditInputRef}
                                  value={editingRecentTitle}
                                  onChange={(e) => setEditingRecentTitle(e.target.value)}
                                  onBlur={() => saveEditedRecentTitle(r)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      saveEditedRecentTitle(r);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingRecentSessionKey(null);
                                      setEditingRecentTitle(title);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    height: 28,
                                    borderRadius: 6,
                                    border: `1px solid ${mode === 'dark' ? '#444' : '#ccc'}`,
                                    background: mode === 'dark' ? '#111' : '#fff',
                                    color: colors.text,
                                    padding: '0 8px',
                                    fontSize: 13,
                                    fontFamily: 'Urbanist, Arial, sans-serif'
                                  }}
                                />
                              ) : (
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {title}
                                </span>
                              )}
                            </button>

                            {!isEditingRecent && sessionKey && (
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuProjectId(null);
                                    setOpenMenuRecentSessionKey((prev) =>
                                      prev === sessionKey ? null : sessionKey
                                    );
                                  }}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    border: 'none',
                                    borderRadius: 6,
                                    display: showRecentActions ? 'flex' : 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background:
                                      mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(0,0,0,0.06)',
                                    color: colors.stroke,
                                    cursor: 'pointer',
                                    flexShrink: 0
                                  }}
                                >
                                  <KebabIcon color={colors.stroke} size={14} />
                                </button>

                                {openMenuRecentSessionKey === sessionKey && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      position: 'absolute',
                                      top: 26,
                                      right: 0,
                                      minWidth: 120,
                                      borderRadius: 8,
                                      border:
                                        mode === 'dark' ? '1px solid #333' : '1px solid #e6e6e6',
                                      background: mode === 'dark' ? '#1a1a1a' : '#fff',
                                      boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                                      zIndex: 20,
                                      padding: 6
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startEditRecent(r);
                                      }}
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: 6,
                                        color: colors.text,
                                        cursor: 'pointer',
                                        fontSize: 13
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteRecentSession(r);
                                      }}
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: 6,
                                        color: '#f44336',
                                        cursor: 'pointer',
                                        fontSize: 13
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Project Popup (single field: Project title) */}
      {showProjectCreate && (
        <>
          <div
            role="presentation"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2100,
              backdropFilter: 'blur(2px)'
            }}
            onClick={closeProjectCreate}
          />

          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              backgroundColor: colors.bg,
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              zIndex: 2101,
              padding: 24
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 16,
                  border: mode === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)'
                }}
              >
                <span style={{ color: '#26C3C0', fontSize: 20, fontWeight: 800, lineHeight: 1 }}>+</span>
              </div>

              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 600,
                    color: colors.text,
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}
                >
                  Create Project
                </h3>
                <div style={{ fontSize: 13, color: colors.muted, marginTop: 4 }}>
                  Enter a project title to continue.
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: 6,
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}
              >
                Project title
              </label>

              <input
                value={projectTitle}
                onChange={(e) => {
                  setProjectTitle(e.target.value);
                  if (projectTitleError) setProjectTitleError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitProjectCreate();
                  if (e.key === 'Escape') closeProjectCreate();
                }}
                placeholder="e.g. Marketing Website"
                style={{
                  width: '100%',
                  height: 44,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                  backgroundColor: mode === 'dark' ? '#111' : '#fff',
                  color: mode === 'dark' ? '#fff' : '#000',
                  outline: 'none',
                  fontSize: 14,
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}
              />

              {projectTitleError && (
                <div
                  style={{
                    marginTop: 8,
                    color: '#f44336',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}
                >
                  {projectTitleError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                type="button"
                onClick={closeProjectCreate}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                  background: 'transparent',
                  color: mode === 'dark' ? '#fff' : '#222',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitProjectCreate}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: 'none',
                  backgroundColor: isCreatingProject ? '#1ba29f' : '#26C3C0',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: isCreatingProject ? 'default' : 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}
                disabled={isCreatingProject}
              >
                {isCreatingProject ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}

